import type { Scope } from "@/domain/social/SocialRepository";

export type LocalNoteVisibility = "private" | "public";

export interface LocalNoteMediaAsset {
	readonly id: string;
	readonly filename: string;
	readonly mimeType: string;
	readonly blob: Blob;
}

export interface LocalNoteAsset {
	readonly id: string;
	readonly ownerUserId: string;
	readonly scopeEpoch: number;
	readonly authorName: string;
	readonly body: string;
	readonly visibility: LocalNoteVisibility;
	readonly media: readonly LocalNoteMediaAsset[];
	readonly likeCount: number;
	readonly isLiked: boolean;
	readonly createdAt: number;
	readonly updatedAt: number;
}

export interface CreateLocalNoteInput {
	readonly body: string;
	readonly visibility: LocalNoteVisibility;
	readonly authorName: string;
	readonly mediaFiles: readonly File[];
}

const DATABASE_NAME = "artist-local-social-media-v1";
const DATABASE_VERSION = 1;
const NOTE_STORE = "notes";

interface StoredLocalNoteAsset extends LocalNoteAsset {
	readonly key: string;
}

function scopePrefix(scope: Scope): string {
	return `${scope.ownerUserId}:${scope.scopeEpoch}:`;
}

function noteKey(scope: Scope, id: string): string {
	return `${scopePrefix(scope)}${id}`;
}

function newId(prefix: string): string {
	const suffix =
		typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	return `${prefix}-${suffix}`;
}

export async function createLocalNote(
	scope: Scope,
	input: CreateLocalNoteInput,
): Promise<LocalNoteAsset> {
	const body = input.body.trim();
	if (body.length === 0) throw new TypeError("note body is required");
	const now = Date.now();
	const id = newId("note");
	const stored: StoredLocalNoteAsset = {
		key: noteKey(scope, id),
		id,
		ownerUserId: scope.ownerUserId,
		scopeEpoch: scope.scopeEpoch,
		authorName: input.authorName.trim() || "Artist",
		body,
		visibility: input.visibility,
		media: input.mediaFiles.slice(0, 9).map((file) => ({
			id: newId("note-media"),
			filename: file.name,
			mimeType: file.type || "application/octet-stream",
			blob: file,
		})),
		likeCount: 0,
		isLiked: false,
		createdAt: now,
		updatedAt: now,
	};
	await putStored(stored);
	return stripKey(stored);
}

export async function listLocalNotes(scope: Scope): Promise<readonly LocalNoteAsset[]> {
	const database = await openDatabase();
	return new Promise<readonly LocalNoteAsset[]>((resolve, reject) => {
		const transaction = database.transaction(NOTE_STORE, "readonly");
		const request = transaction.objectStore(NOTE_STORE).getAll();
		request.onsuccess = () => {
			const notes = (request.result as StoredLocalNoteAsset[])
				.filter(
					(note) =>
						note.ownerUserId === scope.ownerUserId &&
						note.scopeEpoch === scope.scopeEpoch,
				)
				.sort((left, right) => right.updatedAt - left.updatedAt)
				.map(stripKey);
			resolve(notes);
		};
		request.onerror = () =>
			reject(request.error ?? new Error("local-note-list-failed"));
		transaction.oncomplete = () => database.close();
		transaction.onerror = () => database.close();
	});
}

export async function loadLocalNote(
	scope: Scope,
	id: string,
): Promise<LocalNoteAsset | null> {
	const stored = await loadStored(scope, id);
	return stored === null ? null : stripKey(stored);
}

export async function setLocalNoteLiked(
	scope: Scope,
	id: string,
	liked: boolean,
): Promise<LocalNoteAsset> {
	const current = await loadStored(scope, id);
	if (current === null) throw new Error("local-note-not-found");
	const next: StoredLocalNoteAsset = {
		...current,
		isLiked: liked,
		likeCount: Math.max(
			0,
			current.likeCount + (liked === current.isLiked ? 0 : liked ? 1 : -1),
		),
		updatedAt: Date.now(),
	};
	await putStored(next);
	return stripKey(next);
}

export async function deleteLocalNote(scope: Scope, id: string): Promise<void> {
	const database = await openDatabase();
	await new Promise<void>((resolve, reject) => {
		const transaction = database.transaction(NOTE_STORE, "readwrite");
		transaction.objectStore(NOTE_STORE).delete(noteKey(scope, id));
		transaction.oncomplete = () => {
			database.close();
			resolve();
		};
		transaction.onerror = () => {
			const error = transaction.error ?? new Error("local-note-delete-failed");
			database.close();
			reject(error);
		};
	});
}

async function loadStored(
	scope: Scope,
	id: string,
): Promise<StoredLocalNoteAsset | null> {
	const database = await openDatabase();
	return new Promise<StoredLocalNoteAsset | null>((resolve, reject) => {
		const transaction = database.transaction(NOTE_STORE, "readonly");
		const request = transaction.objectStore(NOTE_STORE).get(noteKey(scope, id));
		request.onsuccess = () =>
			resolve((request.result as StoredLocalNoteAsset | undefined) ?? null);
		request.onerror = () =>
			reject(request.error ?? new Error("local-note-read-failed"));
		transaction.oncomplete = () => database.close();
		transaction.onerror = () => database.close();
	});
}

async function putStored(value: StoredLocalNoteAsset): Promise<void> {
	const database = await openDatabase();
	await new Promise<void>((resolve, reject) => {
		const transaction = database.transaction(NOTE_STORE, "readwrite");
		transaction.objectStore(NOTE_STORE).put(value);
		transaction.oncomplete = () => {
			database.close();
			resolve();
		};
		transaction.onerror = () => {
			const error = transaction.error ?? new Error("local-note-write-failed");
			database.close();
			reject(error);
		};
	});
}

function openDatabase(): Promise<IDBDatabase> {
	if (typeof indexedDB === "undefined") {
		return Promise.reject(new Error("indexeddb-unavailable"));
	}
	return new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
		request.onupgradeneeded = () => {
			const database = request.result;
			if (!database.objectStoreNames.contains(NOTE_STORE)) {
				database.createObjectStore(NOTE_STORE, { keyPath: "key" });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () =>
			reject(request.error ?? new Error("local-note-database-open-failed"));
	});
}

function stripKey(value: StoredLocalNoteAsset): LocalNoteAsset {
	const { key: _key, ...note } = value;
	return {
		...note,
		media: note.media.map((asset) => ({ ...asset })),
	};
}
